/**
 * Org / publication URL switching.
 *
 * Ports the legacy behavior from
 * `app/javascript/app/components/nav/account_switcher_loader.js` on master:
 *
 *  - On account change: try to rewrite an account-scoped URL in place
 *    (`/organizations/:id`, `account_id=:id`). If the current URL isn't
 *    account-scoped, fall back to the new account's first publication and
 *    rewrite a publication-scoped URL.
 *  - On publication change: rewrite publication-scoped URL segments
 *    (`/publications/:id`, `brand_profile_id=:id`, `publication_id=:id`,
 *    `/analytics/:id`).
 *  - Both strip a trailing `/:id` or `/:id/edit` because resources scoped
 *    to the old parent don't exist under the new one.
 *
 * Both paths finish with a full page reload (`window.location = ...`)
 * to match legacy — the Rails side re-resolves permissions/layouts on
 * the new scope.
 */

// Matches /organizations/:id OR account_id=:id
const ACCOUNT_ID_MATCHER = /(\/organizations\/)(\d*)|(account_id=)(\d*)/;

// Matches /publications/:id, brand_profile_id=:id, publication_id=:id, /analytics/:id
const PUBLICATION_ID_MATCHER =
  /(\/publications\/)(\d*)|(brand_profile_id=)(\d*)|(publication_id=)(\d*)|(\/analytics\/)(\d*)/;

// Strip a trailing /:id or /:id/edit
const RESOURCE_ID_MATCHER = /\/\d+$|\/\d+\/edit$/;

function removeURLParameter(url: string, paramTarget: string): string {
  const [base, query] = url.split('?');
  if (!query) return url;

  const prefix = `${encodeURIComponent(paramTarget)}=`;
  const params = query.split(/[&;]/g).filter((p) => !p.startsWith(prefix));
  return params.length ? `${base}?${params.join('&')}` : base;
}

function accountIdFromLocation(): string | undefined {
  const { pathname, search } = window.location;
  const match = (pathname + search).match(ACCOUNT_ID_MATCHER) || [];
  return match[2] || match[4];
}

function publicationIdFromLocation(): string | undefined {
  const { pathname, search } = window.location;
  const match = (pathname + search).match(PUBLICATION_ID_MATCHER) || [];
  return match[2] || match[4] || match[6] || match[8];
}

/**
 * If the current URL is account-scoped, rewrite the account segment in
 * place and trigger a full reload. Returns true when it fired.
 */
export function handleAccountScopedPage(accountId: number): boolean {
  if (!accountIdFromLocation()) return false;

  let newLocation = window.location.href.replace(
    ACCOUNT_ID_MATCHER,
    `$1$3${accountId}`,
  );
  newLocation = newLocation.replace(RESOURCE_ID_MATCHER, '');

  window.location.href = newLocation;
  return true;
}

/**
 * If the current URL is publication-scoped, rewrite the publication
 * segment in place and trigger a full reload. Returns true when it fired.
 */
export function handlePublicationScopedPage(publicationId: number): boolean {
  if (!publicationIdFromLocation()) return false;

  let newLocation = window.location.href.replace(
    PUBLICATION_ID_MATCHER,
    `$1$3$5$7${publicationId}`,
  );
  // Analytics segment IDs (s_id) are per-publication — drop them on switch
  newLocation = removeURLParameter(newLocation, 's_id');
  newLocation = newLocation.replace(RESOURCE_ID_MATCHER, '');

  window.location.href = newLocation;
  return true;
}

/**
 * Switch to a different organization (account).
 *
 * Behavior mirrors legacy:
 *   1. If the URL is account-scoped (e.g. /organizations/:id/portals),
 *      rewrite in place.
 *   2. Otherwise look up the new account's first publication and rewrite
 *      the publication-scoped URL.
 *   3. As a last resort, navigate to the publication landing.
 *
 * The BE lookup goes through the existing `/organizations/:id.json` endpoint,
 * which already returns `publications` ordered by name — `publications[0]`
 * is the "first" publication in legacy's sense.
 */
export async function switchAccount(newAccountId: number): Promise<void> {
  // Legacy parity (nav.jsx:112): set the cookie so Rails-side requests
  // know the new active account on subsequent hits.
  document.cookie = `activeAccountId=${newAccountId}; path=/`;

  if (handleAccountScopedPage(newAccountId)) return;

  let firstPubId: number | undefined;
  try {
    const res = await fetch(`/organizations/${newAccountId}.json`, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });
    if (res.ok) {
      const data = await res.json();
      firstPubId = data.publications?.[0]?.id;
    }
  } catch {
    // fall through — pub-agnostic surface fallback below
  }

  if (firstPubId && handlePublicationScopedPage(firstPubId)) return;

  // Pub-agnostic surface (e.g. /media, /tasks, /messages) — legacy nav.jsx:
  // 113-114 returned without redirect when neither account-scoped nor pub-
  // scoped URL rewrite applied (setState in place + nav re-render). Hard-
  // redirecting to /publications/:id instead bounces Rails to that pub's
  // default landing page (Content Strategy), which the user reported as
  // a regression on the Media library (sheet row 93, Nick Vaka). Preserve
  // the surface by reloading the current pathname with account_id (and
  // brand_profile_id when known) as URL params — AssettingsController#
  // redirect_with_account_id and the Media page's `initFilter('brand_
  // profile_id')` both consume these on remount.
  const url = new URL(window.location.href);
  url.searchParams.set('account_id', String(newAccountId));
  if (firstPubId) {
    url.searchParams.set('brand_profile_id', String(firstPubId));
    document.cookie = `activePublicationId=${firstPubId}; path=/`;
  }
  window.location.href = url.toString();
}

/**
 * Switch to a different publication.
 *
 * Rewrites the publication-scoped URL in place; falls back to preserving
 * the current pub-agnostic surface with `brand_profile_id` re-scoped.
 */
export function switchPublication(newPublicationId: number): void {
  // Legacy parity (nav.jsx:127): set the cookie so Rails-side requests
  // know the new active pub on subsequent hits.
  document.cookie = `activePublicationId=${newPublicationId}; path=/`;

  if (handlePublicationScopedPage(newPublicationId)) return;

  // Pub-agnostic surface (e.g. /media, /tasks, /messages). See switchAccount
  // above for the rationale — legacy stayed on the surface, the canary
  // regression hard-redirected to /publications/:id and bounced to Content
  // Strategy. We preserve the current pathname and re-scope via
  // `brand_profile_id` query param. AssettingsController#redirect_with_
  // account_id will derive the matching `account_id` from the brand profile
  // on the next HTML hit; `MediaLibraryPage`'s `initFilter('brand_profile_id')`
  // (and other surfaces that read it) will pick up the new scope on remount.
  const url = new URL(window.location.href);
  url.searchParams.set('brand_profile_id', String(newPublicationId));
  // Drop any stale account_id so Rails re-derives from the new pub.
  url.searchParams.delete('account_id');
  window.location.href = url.toString();
}
